import { GitBranch, Snowflake } from 'lucide-react';
import { Link } from 'react-router';
import type { KitOffer } from '../api/types.js';
import { useCart } from '../cart/cart-context.js';
import { formatPrice, searchHref } from '../lib/format.js';
import { iconForKit } from '../lib/kit-icons.js';
import { Badge } from './badge.js';

function stockLabel(stock: number): string {
  if (stock === 0) return 'Sin stock';
  return stock === 1 ? '1 disponible' : `${String(stock)} disponibles`;
}

export function KitCard({ kit }: { kit: KitOffer }) {
  const cart = useCart();
  const inCart = cart.quantities[kit.sku] ?? 0;
  const canAdd = kit.price !== null && inCart < kit.stock;
  const KitIcon = iconForKit(kit.sku);
  return (
    <article className="flex flex-col gap-4 rounded-card border border-border bg-card p-5">
      <div className="flex items-start gap-3">
        <span className="rounded-control bg-primary/10 p-2 text-primary">
          <KitIcon aria-hidden="true" className="size-6" />
        </span>
        <div className="min-w-0">
          <h2 className="text-lg leading-snug font-semibold text-card-foreground">{kit.name}</h2>
          <p className="text-sm text-muted-foreground">{kit.sku}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Badge tone={kit.stock > 0 ? 'success' : 'neutral'}>{stockLabel(kit.stock)}</Badge>
        {kit.isCold && (
          <Badge tone="accent">
            <Snowflake aria-hidden="true" className="size-4" />
            Refrigerado
          </Badge>
        )}
      </div>
      <Link
        to={searchHref(kit.sku)}
        className="inline-flex min-h-11 items-center gap-1 self-start text-sm font-medium text-accent underline-offset-2 hover:underline"
      >
        <GitBranch aria-hidden="true" className="size-4" />
        Ver lotes y su origen
      </Link>
      <div className="mt-auto flex items-end justify-between gap-3">
        <p className="text-2xl font-bold text-foreground">
          {kit.price === null ? (
            <span className="text-base text-muted-foreground">Precio no disponible</span>
          ) : (
            formatPrice(kit.price)
          )}
        </p>
        <button
          type="button"
          disabled={!canAdd}
          onClick={() => {
            cart.add(kit.sku, kit.stock);
          }}
          className="min-h-11 cursor-pointer rounded-control bg-accent px-4 font-medium text-on-accent transition-opacity duration-150 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {inCart > 0 ? `En el carro (${String(inCart)})` : 'Agregar al carro'}
        </button>
      </div>
    </article>
  );
}
